import { apiClient } from "@niutraan/hooks/src/lib/api";
import type {
  CreateUserDto,
  UpdateUserDto,
  UserDto,
  UsersResponse,
} from "@niutraan/shared-dto";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

// Query keys
const QUERY_KEYS = {
  users: "users",
};

// Get all users with pagination
export const useUsers = (page = 1, limit = 10, search?: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.users, page, limit, search],
    queryFn: async () => {
      try {
        const queryParams = new URLSearchParams();
        queryParams.append("page", page.toString());
        queryParams.append("limit", limit.toString());
        if (search) {
          queryParams.append("search", search);
        }

        return await apiClient.get<UsersResponse>(
          `/users?${queryParams.toString()}`,
        );
      } catch (error) {
        console.error("Error fetching users:", error);
        throw error;
      }
    },
  });
};

// Get user by ID
export const useUserById = (id: string) => {
  return useQuery({
    queryKey: [QUERY_KEYS.users, id],
    queryFn: async () => {
      try {
        return await apiClient.get<UserDto>(`/users/${id}`);
      } catch (error) {
        console.error(`Error fetching user with ID ${id}:`, error);
        throw error;
      }
    },
    enabled: !!id,
  });
};

// Create user
export const useCreateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (newUser: CreateUserDto) =>
      apiClient.post<UserDto>("/users", newUser),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.users] });
    },
    onError: (error) => {
      console.error("Error creating user:", error);
    },
  });
};

// Update user
export const useUpdateUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateUserDto }) =>
      apiClient.patch<UserDto>(`/users/${id}`, data),
    onSuccess: (_, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.users] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.users, variables.id],
      });
    },
    onError: (error) => {
      console.error("Error updating user:", error);
    },
  });
};

// Delete user
export const useDeleteUser = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => apiClient.delete<void>(`/users/${id}`),
    onSuccess: () => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.users] });
    },
    onError: (error) => {
      console.error("Error deleting user:", error);
    },
  });
};

// Update user roles
export const useUpdateUserRoles = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, roles }: { id: string; roles: string[] }) =>
      apiClient.put<UserDto>(`/users/${id}/roles`, { roles }),
    onSuccess: (_, variables) => {
      // Invalidate and refetch
      queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.users] });
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.users, variables.id],
      });
    },
    onError: (error) => {
      console.error("Error updating user roles:", error);
    },
  });
};
